import { useState, useEffect } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	Alert,
	Link,
	Tabs,
	Tab,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material'
import { useAuth } from '../contexts/auth-context'
import { supabase } from '../lib/supabase'

export function AuthPage() {
	const [tab, setTab] = useState(0)
	const { user, roleCheckComplete } = useAuth()
	const navigate = useNavigate()

	useEffect(() => {
		if (user && roleCheckComplete) {
			navigate('/', { replace: true })
		}
	}, [user, roleCheckComplete, navigate])

	return (
		<Box
			mt={20}
			paddingTop='5rem'
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="60vh"
			maxWidth="sm"
			margin='auto'
		>
			<Card
				elevation={4}
				sx={{
					borderRadius: 3,
					px: 6,
					py: 8,
					width: '100%',
					textAlign: 'center',
				}}
			>
				<CardContent>
					<Typography variant="h4" fontWeight="bold" gutterBottom>
						Aquatic Labs Sensor Monitor
					</Typography>
					<Tabs
						value={tab}
						onChange={(_, newValue) => setTab(newValue)}
						centered
						sx={{ mb: 3 }}
					>
						<Tab label="Sign In" />
						<Tab label="Sign Up" />
					</Tabs>
					{tab === 0 ? <SignInForm /> : <SignUpForm />}
					<Box sx={{ mt: 2, textAlign: 'center' }}>
						<Typography variant="body2" color="text.secondary">
							<Link component={RouterLink} to="/forgot-password" underline="hover">
								Forgot your password?
							</Link>
						</Typography>
					</Box>
				</CardContent>
			</Card>
		</Box>
	)
}

function SignInForm() {
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState(null)
	const [isSigningIn, setIsSigningIn] = useState(false)
	const { signIn, loading, roleCheckComplete } = useAuth()

	useEffect(() => {
		if (isSigningIn && !loading && roleCheckComplete) {
			setIsSigningIn(false)
		}
	}, [isSigningIn, loading, roleCheckComplete])

	const handleSubmit = async (e) => {
		e.preventDefault()
		setError(null)
		setIsSigningIn(true)

		const { error: signInError } = await signIn(email, password)

		if (signInError) {
			setError(signInError.message)
			setIsSigningIn(false)
		}
	}

	return (
		<Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left' }}>
			<TextField
				fullWidth
				id="email"
				label="Email"
				type="email"
				placeholder="your@email.com"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				required
				autoComplete="email"
				margin="normal"
			/>
			<TextField
				fullWidth
				id="password"
				label="Password"
				type="password"
				placeholder="Enter your password"
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				required
				autoComplete="current-password"
				margin="normal"
			/>
			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}
			<Button
				type="submit"
				variant="contained"
				fullWidth
				disabled={isSigningIn}
				sx={{ mt: 3, py: 1.5 }}
			>
				{isSigningIn ? 'Signing in...' : 'Sign In'}
			</Button>
		</Box>
	)
}

function SignUpForm() {
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [error, setError] = useState(null)
	const [success, setSuccess] = useState(false)
	const [isSigningUp, setIsSigningUp] = useState(false)
	const [showVerifyDialog, setShowVerifyDialog] = useState(false)

	const handleSubmit = async (e) => {
		e.preventDefault()
		setError(null)
		setSuccess(false)

		if (password !== confirmPassword) {
			setError('Passwords do not match')
			return
		}

		if (password.length < 6) {
			setError('Password must be at least 6 characters long')
			return
		}

		setIsSigningUp(true)

		try {
			const { data, error: signUpError } = await supabase.auth.signUp({
				email,
				password,
				options: {
					emailRedirectTo: `${window.location.origin}/`,
				}
			})

			if (signUpError) {
				setError(signUpError.message)
				setIsSigningUp(false)
				return
			}

			if (data?.user && !data?.session) {
				// Email confirmation required — show a full-screen dialog
				setShowVerifyDialog(true)
			} else if (data?.session) {
				setSuccess(true)
			}
		} catch (err) {
			setError(err.message || 'An error occurred during signup')
			setIsSigningUp(false)
		}
	}

	return (
		<>
			<Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left' }}>
				<TextField
					fullWidth
					id="signup-email"
					label="Email"
					type="email"
					placeholder="your@email.com"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					required
					autoComplete="email"
					margin="normal"
					disabled={success}
				/>
				<TextField
					fullWidth
					id="signup-password"
					label="Password"
					type="password"
					placeholder="Enter your password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
					autoComplete="new-password"
					margin="normal"
					disabled={success}
					helperText="Minimum 6 characters"
				/>
				<TextField
					fullWidth
					id="confirm-password"
					label="Confirm Password"
					type="password"
					placeholder="Confirm your password"
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					required
					autoComplete="new-password"
					margin="normal"
					disabled={success}
				/>
				{error && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{error}
					</Alert>
				)}
				<Button
					type="submit"
					variant="contained"
					fullWidth
					disabled={isSigningUp || success}
					sx={{ mt: 3, py: 1.5 }}
				>
					{isSigningUp ? 'Creating account...' : 'Sign Up'}
				</Button>
			</Box>
			<Dialog open={showVerifyDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Verify Your Email</DialogTitle>
				<DialogContent>
					<Typography variant="body1" sx={{ mt: 1 }}>
						We&apos;ve sent a verification link to <strong>{email}</strong>. Please check your inbox and click the link to finish creating your account.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowVerifyDialog(false)}>
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
